import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Handles the return from Emergent Auth.
 * URL will contain a fragment: /account#session_id=<token>
 * We exchange it for our own JWT via /api/auth/google/session, then clean the
 * fragment and navigate to /account.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithGoogleSession } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/account", { replace: true });
      return;
    }

    (async () => {
      try {
        await loginWithGoogleSession(sessionId);
        // Clean the fragment out of the URL, keep user on /account
        window.history.replaceState(null, "", "/account");
        navigate("/account", { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || "Google sign-in failed.");
        // NOTE: keep the session_id fragment in the URL for now so App.js
        // continues to render <AuthCallback /> (its Shell short-circuits on
        // 'session_id=' presence). We clean it up right before we redirect.
      }
    })();
  }, [loginWithGoogleSession, navigate]);

  // On error, show it for a beat, then clean the fragment and go to /account
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => {
      window.history.replaceState(null, "", "/account");
      navigate("/account", { replace: true });
    }, 3500);
    return () => clearTimeout(t);
  }, [error, navigate]);

  return (
    <div
      data-testid="auth-callback"
      className="min-h-screen flex items-center justify-center bg-[var(--gl-bg)] px-6"
    >
      <div className="text-center">
        <div className="font-badge text-[11px] tracking-[0.32em] text-[var(--gl-orange-hover)]">
          {error ? "SOMETHING WENT WRONG" : "SIGNING YOU IN"}
        </div>
        <div className="font-serif-display text-3xl md:text-4xl mt-3 text-[#241C18]">
          {error ? "Please try again." : "One moment, querida…"}
        </div>
        {error && <p className="mt-4 text-sm text-[#8A7568]">{error}</p>}
      </div>
    </div>
  );
};

export default AuthCallback;
